import { Text } from '@chakra-ui/react';
import {
  OrcaApi,
  ValidateRequest,
  ValidateResponse,
  PENDING_SIGNUP_STATUS,
} from '@orca/api';
import { PlatformsAuthUrlResponse, JWTAccessToken } from '@orca/api-auth';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import Link from '~/components/Link';
import { ORIGIN, CONNECT_PLATFORMS_ACTIONS } from '~/constants';
import { INVITATON_TYPES } from '~/constants/invitationType';
import { PLATFORMS } from '~/constants/platforms';
import { routes } from '~/constants/routes';
import { useAlert } from '~/contexts/alert';
import {
  getAlertFeedBackError,
  AlertError,
} from '~/contexts/alert/alertFeedBackError';
import { useAuth } from '~/contexts/auth';
import { useSignUp } from '~/contexts/signup';
import { captureException } from '~/errorsLogging';
import { LoginLayout } from '~/layouts/LoginLayout';
import { PlatformsAuthEvent } from '~/types';
import { AuthTokens } from '~/types/AuthTokens';
import { fetchAdminInvitationStatus } from '~/utils/fetchAdminInvitationStatus';
import { fetchBrandInvitationStatus } from '~/utils/fetchBrandInvitationStatus';
import { isAuthServiceSSOLoginSuccess } from '~/utils/isAuthServiceSSOLoginSuccess';
import { isAuthServiceSSOSignupError } from '~/utils/isAuthServiceSSOSignupError';
import { isPlatformsAuthSignupSuccessEvent } from '~/utils/isAuthServiceSSOSignupSuccess';

import { SignUpScreenComponent } from './SignUpScreen.component';
import { FormValues } from './SignUpScreen.vm';

interface Props {
  email?: string;
  heading: React.ReactNode;
  platform: PLATFORMS;
  invitationCode?: string | null;
  brandUserSignUpToken?: string | null;
  isFromEcho?: boolean;
  invitationType?: INVITATON_TYPES;
  adminInvitationEmail?: string;
  adminInvitationCode?: string | null;
}

type Url = string | undefined;

export function SignUpScreen({
  email = '',
  heading,
  platform,
  invitationCode,
  brandUserSignUpToken,
  isFromEcho = false,
  invitationType,
  adminInvitationEmail,
  adminInvitationCode,
}: Props) {
  const [googleAuthUrl, setGoogleAuthUrl] = useState<Url | AlertError>(
    undefined
  );
  const [microsoftAuthUrl, setMicrosoftAuthUrl] = useState<Url | AlertError>(
    undefined
  );
  const [facebookAuthUrl, setFacebookAuthUrl] = useState<Url | AlertError>(
    undefined
  );

  const auth = useAuth();
  const signUp = useSignUp();
  const router = useRouter();
  const { setAlertProps } = useAlert();

  useEffect(() => {
    if ((isFromEcho && !signUp.isEcho) || (!isFromEcho && signUp.isEcho)) {
      signUp.setIsEcho(isFromEcho);
    }
  }, [isFromEcho, signUp]);

  useEffect(() => {
    const fetchPlatformsUrl = async () => {
      try {
        const { googleUrl, azureUrl, facebookUrl } =
          (await auth.platformsAuthUrl({
            platform: platform === PLATFORMS.foam ? ORIGIN.foam : ORIGIN.echo,
            action: CONNECT_PLATFORMS_ACTIONS.signup,
          })) as PlatformsAuthUrlResponse;
        setGoogleAuthUrl(googleUrl);
        setMicrosoftAuthUrl(azureUrl);
        setFacebookAuthUrl(facebookUrl);
      } catch (e: any) {
        setGoogleAuthUrl(e);
        setMicrosoftAuthUrl(e);
        setFacebookAuthUrl(e);
        const sentryProps = {
          errorInfo: { e },
          tags: {
            section: 'signupSSOEvent',
          },
        };
        captureException(e, sentryProps);
      }
    };
    fetchPlatformsUrl();
  }, [auth, platform, setAlertProps]);

  useEffect(() => {
    const emailUnmatchInvitationEmailError = () =>
      setAlertProps({
        isVisible: true,
        title: 'Email doesn’t match invite',
        status: 'error',
        description: (
          <>
            The email provided doesn’t match what we have in our system. If you
            want to use a different email, please reach your contact person for
            a new invitation.
          </>
        ),
      });

    const handler = async (event: MessageEvent) => {
      const platformsAuthEvent: PlatformsAuthEvent = event.data;
      if (isAuthServiceSSOSignupError(platformsAuthEvent)) {
        const sentryProps = {
          errorInfo: {
            platformsAuthEvent,
            message: platformsAuthEvent.message,
            errorCode: Number(platformsAuthEvent.code),
          },
          tags: {
            section: 'signupSSOEvent',
          },
        };
        captureException(new Error(platformsAuthEvent.message), sentryProps);
        getAlertFeedBackError(
          {
            message: platformsAuthEvent.message,
            errorCode: Number(platformsAuthEvent.code),
          },
          setAlertProps
        );
      }

      if (isPlatformsAuthSignupSuccessEvent(platformsAuthEvent)) {
        // if the user is not registered, previously checked by the backend ...
        // a signup process takes place, with an event type authservice-sso-foam-signup-success
        if (invitationCode) {
          // if the user signs up from a talent rep to creator invitation link
          if (platform === PLATFORMS.foam) {
            if (email !== platformsAuthEvent.userInfo.email) {
              // if the invitation email doesn't match the auth email
              emailUnmatchInvitationEmailError();
            } else {
              auth.signUpFoamManagedCreatorWithSSO(
                {
                  email: platformsAuthEvent.userInfo.email,
                  googleToken: platformsAuthEvent.tokens.googleToken,
                  azureToken: platformsAuthEvent.tokens.azureToken,
                  facebookToken: platformsAuthEvent.tokens.facebookToken,
                  invitationCode,
                },
                setAlertProps
              );
            }
          }
        } else if (brandUserSignUpToken) {
          // if the user signs up from a brand invitation link
          if (email !== platformsAuthEvent.userInfo.email) {
            // if the invitation email doesn't match the auth email
            emailUnmatchInvitationEmailError();
          } else {
            OrcaApi.brandUsers
              .validate({
                signup_token: brandUserSignUpToken,
                google_token: platformsAuthEvent.tokens.googleToken,
                azure_token: platformsAuthEvent.tokens.azureToken,
                facebook_token: platformsAuthEvent.tokens.facebookToken,
                excludeAuthorizationHeader: true,
              } as ValidateRequest)
              .then((resp: ValidateResponse) => {
                auth.redirectToApp({
                  accessToken: resp.access_token,
                  refreshToken: resp.refresh_token,
                } as AuthTokens);
              })
              .catch(error => {
                getAlertFeedBackError(error, setAlertProps);
              });
          }
        } else if (adminInvitationCode) {
          // if the user signs up from admin invitation link
          if (adminInvitationEmail !== platformsAuthEvent.userInfo.email) {
            // if the invitation email doesn't match the auth email
            emailUnmatchInvitationEmailError();
          } else {
            signUp.setFields({
              ...signUp.fields,
              email: adminInvitationEmail,
              googleToken: platformsAuthEvent.tokens.googleToken,
              azureToken: platformsAuthEvent.tokens.azureToken,
              facebookToken: platformsAuthEvent.tokens.facebookToken,
              adminInvitationCode,
            });
            router.push(routes[platform].signUpNameAndAgency);
          }
        } else {
          const email = platformsAuthEvent.userInfo.email;
          const googleToken = platformsAuthEvent.tokens.googleToken;
          const azureToken = platformsAuthEvent.tokens.azureToken;
          const facebookToken = platformsAuthEvent.tokens.facebookToken;

          const signup_status = await fetchBrandInvitationStatus(email);
          const { signup_status: adminInvitationSignupStatus } =
            await fetchAdminInvitationStatus(email);

          // if the signup_status === 'pending', validate endpoint should be always called
          // @param: email (as mandatory param)
          if (signup_status === PENDING_SIGNUP_STATUS.pending) {
            OrcaApi.brandUsers
              .validate({
                email,
                google_token: googleToken,
                azure_token: azureToken,
                facebook_token: facebookToken,
                excludeAuthorizationHeader: true,
              } as ValidateRequest)
              .then((resp: ValidateResponse) => {
                auth.redirectToApp({
                  accessToken: resp.access_token,
                  refreshToken: resp.refresh_token,
                } as AuthTokens);
              })
              .catch(error => {
                getAlertFeedBackError(error, setAlertProps);
              });
            // Edge case: user previously registered always go through isAuthServiceSSOLoginSuccess event
          } else if (signup_status === PENDING_SIGNUP_STATUS.registered) {
            setAlertProps({
              title: 'An account for this email already exists',
              isVisible: true,
              status: 'error',
              description: (
                <Text textAlign="start">
                  Please {''}
                  <Link
                    onClick={() => {
                      router.push(routes[platform].login);
                    }}
                    fontWeight="medium"
                    textDecoration="underline"
                  >
                    log in
                  </Link>
                  {''} or use different email address
                </Text>
              ),
            });
          } else if (
            adminInvitationSignupStatus === PENDING_SIGNUP_STATUS.pending
          ) {
            router.push({
              pathname: routes.foam.agencyPendingInvitation,
              query: { email },
            });
          } else {
            signUp.setFields({
              email,
              googleToken: googleToken,
              azureToken: azureToken,
              facebookToken: facebookToken,
              managerName: platformsAuthEvent.userInfo.name,
              origin: platform === PLATFORMS.foam ? ORIGIN.foam : ORIGIN.echo,
            });
            router.push(routes[platform].signupAccountType);
          }
        }
      }

      if (isAuthServiceSSOLoginSuccess(platformsAuthEvent)) {
        if (
          (invitationCode || brandUserSignUpToken || adminInvitationCode) &&
          (email !== platformsAuthEvent.userInfo.email ||
            adminInvitationEmail !== platformsAuthEvent.userInfo.email)
        ) {
          // if the invitation email doesn't match the auth email
          emailUnmatchInvitationEmailError();
        } else {
          // if the user is previously registered an auto-login takes place
          // and the backend sends the event type authservice-sso-login-success
          auth.loginWithSSOAccount({
            accessToken: platformsAuthEvent.accessToken,
            refreshToken: platformsAuthEvent.refreshToken,
          } as JWTAccessToken);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [
    auth,
    platform,
    router,
    setAlertProps,
    signUp,
    email,
    invitationCode,
    brandUserSignUpToken,
    adminInvitationCode,
    adminInvitationEmail,
  ]);

  const handleSubmit = async (fields: FormValues) => {
    const { email, password } = fields;

    const signup_status = await fetchBrandInvitationStatus(email);
    const { signup_status: adminInvitationSignupStatus } =
      await fetchAdminInvitationStatus(adminInvitationEmail || email);

    if (
      // if user is signing up from a agency invitation link (talent rep to creator), pending status will return 404
      signup_status === PENDING_SIGNUP_STATUS.notFound &&
      invitationCode &&
      platform === PLATFORMS.foam
    ) {
      await auth.signUpFoamManagedCreatorWithPassword(
        {
          email,
          password,
          invitationCode,
        },
        setAlertProps
      );
    } else if (
      // if user is signing up from normal signup view, but has a pending brand invitation...
      signup_status === PENDING_SIGNUP_STATUS.pending &&
      !brandUserSignUpToken
    ) {
      OrcaApi.brandUsers
        .validate({
          email,
          password,
          excludeAuthorizationHeader: true,
        } as ValidateRequest)
        .then(() => {
          router.push({
            pathname: routes.echo.verifyEmail,
            query: { email },
          });
        })
        .catch(error => {
          getAlertFeedBackError(error, setAlertProps);
        });
    } else if (brandUserSignUpToken && platform === PLATFORMS.echo) {
      // if the user is signing up from brand invitation link, orca-api validate endpoint should always be called
      // @param: signup_token (as mandatory param in this case)
      // onSuccess >> redirects to Echo
      OrcaApi.brandUsers
        .validate({
          signup_token: brandUserSignUpToken,
          password,
          excludeAuthorizationHeader: true,
        } as ValidateRequest)
        .then((resp: ValidateResponse) => {
          auth.redirectToApp({
            accessToken: resp.access_token,
            refreshToken: resp.refresh_token,
          } as AuthTokens);
        })
        .catch(error => {
          getAlertFeedBackError(error, setAlertProps);
        });
    } else if (adminInvitationCode && platform === PLATFORMS.foam) {
      // if the user is signing up from admin invitation link, storage metadata and redirect to SSO SignUpNameAndAgency view
      signUp.setFields({
        ...signUp.fields,
        email: adminInvitationEmail,
        password,
        adminInvitationCode,
      });
      router.push(routes.foam.signUpNameAndAgency);
    } else if (
      adminInvitationSignupStatus === PENDING_SIGNUP_STATUS.pending &&
      !invitationCode
    ) {
      router.push({
        pathname: routes.foam.agencyPendingInvitation,
        query: { email },
      });
    } else {
      // normal signup process from signup view
      try {
        const checkEmail = await auth.checkEmailExists({
          email,
          platform,
        });
        if (checkEmail.status) {
          // If the email status exists and the password is correct, an autologin is executed.
          await auth.signUpAutoLogin({
            email: email.toLowerCase(),
            password,
            platform: platform === PLATFORMS.foam ? ORIGIN.foam : ORIGIN.echo,
          });
        }
      } catch (error: any) {
        if (error.message === '102') {
          // Email doesn't exists
          signUp.setFields({
            email: fields.email.toLowerCase(),
            password: fields.password,
            origin: platform === PLATFORMS.foam ? ORIGIN.foam : ORIGIN.echo,
          });
          router.push(routes[platform].signupAccountType);
        } else if (error.message === '104') {
          // Email and password don't match
          signUp.setFields({
            ...signUp.fields,
            email: fields.email.toLowerCase(),
          });
          setAlertProps({
            title: 'An account for this email already exists',
            isVisible: true,
            status: 'error',
            description: (
              <Text textAlign="start">
                Please {''}
                <Link
                  onClick={() => {
                    router.push(routes[platform].login);
                  }}
                  fontWeight="medium"
                  textDecoration="underline"
                >
                  log in
                </Link>
                {''} or use different email address
              </Text>
            ),
          });
        } else if (error.message === '108') {
          router.push({
            pathname: routes.echo.verifyEmail,
            query: { email },
          });
        }
      }
    }
  };

  return (
    <>
      <LoginLayout platform={platform}>
        <SignUpScreenComponent
          heading={heading}
          email={email}
          isInvitation={
            !!invitationCode || !!brandUserSignUpToken || !!adminInvitationCode
          }
          onSubmit={handleSubmit}
          googleAuthUrl={googleAuthUrl}
          microsoftAuthUrl={microsoftAuthUrl}
          facebookAuthUrl={facebookAuthUrl}
          platform={platform}
          invitationType={invitationType}
          adminInvitationEmail={adminInvitationEmail}
        />
      </LoginLayout>
    </>
  );
}
